import { Alert, Button, Loader, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Route, Routes } from "react-router";
import { Panel } from "./Panel";
import { RolesContext } from "./auth/roles";
import { AuthShell } from "./auth/AuthShell";
import { SignIn } from "./auth/SignIn";
import { signOut } from "./auth/session";
import { useSession } from "./auth/useSession";
import { SettingsPage } from "./settings/SettingsPage";
import { ShowPage } from "./shows/ShowPage";
import { ShowsPage } from "./shows/ShowsPage";
import styles from "./App.module.scss";

/**
 * The host panel's root: sign-in, or the panel (spec §4.5, `R-HOST-1`).
 *
 * Four states, and the third is the one worth naming. A member of staff sees the panel; a person with
 * a valid session and **no staff role** sees a plain message saying so, not a sign-in form and not an
 * empty panel. That is `R-HOST-2` showing up in the UI: a staff account is also a customer account, so
 * an ordinary customer signing in on this hostname is a perfectly normal thing to happen and is not
 * an authentication failure.
 *
 * Three of the four states are drawn on the same centred surface (`AuthShell`), which is why the
 * transition from "checking" to "sign in" does not move anything on the screen.
 *
 * What the client decides here is only what to *render*. Every panel route checks the role again,
 * server-side, on every request — see `packages/functions/src/lib/gate.ts`. A client that enforced its
 * own permissions would be a client that can be edited.
 */
export function App() {
  const { t } = useTranslation();
  const { state, loading, error, refresh } = useSession();

  // Loading is a real state, not a default of signed-out. Rendering the sign-in form for the few
  // hundred milliseconds the session check takes would flash a form at somebody who is already
  // signed in, on every single page load.
  if (loading) {
    return (
      <AuthShell>
        <Stack align="center" gap="sm" py="lg">
          <Loader size="sm" color="ink" />
          <Text size="sm" c="dimmed">
            {t("app.checking")}
          </Text>
        </Stack>
      </AuthShell>
    );
  }

  if (error) {
    return (
      <AuthShell>
        <Stack gap="md">
          <Alert color="red" variant="light" radius="md">
            {t("app.unreachable")}
          </Alert>
          <Button variant="default" fullWidth onClick={() => void refresh()}>
            {t("app.retry")}
          </Button>
        </Stack>
      </AuthShell>
    );
  }

  if (!state?.signedIn) {
    return (
      <AuthShell>
        <SignIn onSignedIn={refresh} />
      </AuthShell>
    );
  }

  if (!state.panel) {
    return (
      <AuthShell
        // No `c` prop: the colour comes from the shell's `.footnote` class, which sets one that is
        // legible on the brand yellow. Mantine's `dimmed` is inline-styled and would win over it.
        footnote={<Text size="xs">{t("app.noAccessFootnote")}</Text>}
      >
        <Stack gap="md">
          <Stack gap={4}>
            <Text fw={650} size="lg" c="ink.8">
              {t("app.noAccessHeading")}
            </Text>
            <Text size="sm" c="dimmed">
              {t("app.noAccessBody", { email: state.account.email })}
            </Text>
          </Stack>
          {/* Without this the state is a dead end: somebody who signed in with the wrong address
              could neither reach the panel nor get back to the form to try the right one. */}
          <Button
            variant="default"
            fullWidth
            onClick={() => void signOut().then(refresh)}
          >
            {t("app.signOut")}
          </Button>
        </Stack>
      </AuthShell>
    );
  }

  /**
   * The router starts here rather than around the whole tree, and only once there is a panel to
   * route inside. Everything above this point is one screen with no address of its own: a
   * sign-in form is not a place, and giving it a URL would mean a signed-in member of staff could
   * navigate back to it.
   *
   * `BrowserRouter` and not a hash — the panel is served by CloudFront over an S3 origin with a
   * single-page fallback (`infra/web.ts`), so a deep link to a show resolves. A hash would work
   * too and would read as a workaround for a problem this stack does not have.
   */
  return (
    <div className={styles.shell}>
      <BrowserRouter>
        <Panel account={state.account} roles={state.roles} onSignedOut={refresh}>
          {/* Spec §3's two roles, to the pages that show different things to each. The routes
              are what enforce the difference; this is what stops a moderator being offered a
              control that would refuse them. See `auth/roles.ts`. */}
          <RolesContext value={state.roles}>
            <Routes>
              <Route path="/" element={<ShowsPage />} />
              <Route path="/shows/:showId" element={<ShowPage />} />
              {/* M1.13 — the minimum stock, the default share, and the catalogue's health. */}
              <Route path="/settings" element={<SettingsPage />} />
              {/* Anything else is the list. A panel has no useful 404 — every address in it is
                  one this application generated. */}
              <Route path="*" element={<ShowsPage />} />
            </Routes>
          </RolesContext>
        </Panel>
      </BrowserRouter>
    </div>
  );
}
